import { h, Component, ComponentType } from "preact";

import { deepDiffer } from "../../foundation/utils/diff";

type DynamicImportType<K> = Promise<K>;

interface Props {
    children: JSX.Element[];
    suspense: JSX.Element;
    onLoad?: (component: string) => void;
}

type RouteProps<T, K> = {
    name: string;
    condition: boolean;
    defaultView?: boolean;
    props?: unknown;
} & ({
    component: (props?: T) => ComponentType<T>;
    eager: true;
} | {
    component: (props?: T) => DynamicImportType<K>;
    eager?: false;
});

interface State {
    loadedComponent: { name: null | string; props?: unknown; };
}

const EMPTY_STATE = { name: null, props: null };
const ERROR_MSG = "You should provide a proper suspense fallback to provide a loading indicator or placeholder to display.";

function getSelectedRoute(children: JSX.Element[]) {
    return children.find(({ props }) => props.condition) ??
        children.find(({ props }) => props.defaultView);
}

class LazyRouter extends Component<Props, State> {
    componentToRender: ComponentType | null = null;

    override state = {
        loadedComponent: EMPTY_STATE // this stands for representing loaded actual component's name and prop on every UI state.
    };

    async loadLazyComponent() {
        const { loadedComponent } = this.state;
        const { children } = this.props;
        const selectedRoute = getSelectedRoute(children);
        const {
            eager,
            name: componentName,
            component: loadComponent,
            props: componentProps
        } = selectedRoute?.props ?? {};

        if (!componentName) {
            this.componentToRender = null;

            if (loadedComponent.name) {
                this.setState({ loadedComponent: EMPTY_STATE });
            }

            return;
        }

        if (loadedComponent.name === componentName) {
            if (deepDiffer(componentProps, loadComponent.props)) {
                this.setState({ loadedComponent: { name: componentName, props: componentProps } });
            }

            return;
        }

        this.componentToRender = eager
            ? loadComponent()
            : (await loadComponent()).default;

        this.setState({ loadedComponent: { name: componentName, props: componentProps } });
    }

    override componentDidMount() {
        this.loadLazyComponent();
    }

    override shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>): boolean {
        const { props: { component: _c1, ...prevCompProps } } = getSelectedRoute(this.props.children) ?? { props: {} };
        const { props: { component: _c2, ...nextCompProps } } = getSelectedRoute(nextProps.children) ?? { props: {} };

        return deepDiffer(prevCompProps, nextCompProps) || deepDiffer(this.state, nextState);
    }

    override componentDidUpdate(_: Props, prevState: State) {
        const { onLoad, suspense } = this.props;
        const { loadedComponent } = this.state;

        if (!suspense) {
            throw new Error(ERROR_MSG);
        }

        this.loadLazyComponent();

        // do not trigger onLoad callback for already loaded components.
        if (onLoad && prevState.loadedComponent.name !== loadedComponent.name) {
            onLoad(loadedComponent.name!);
        }
    }

    render() {
        const { suspense } = this.props;
        const { loadedComponent: { props } } = this.state;
        const ComponentToRender = this.componentToRender;

        if (!ComponentToRender) {
            return suspense;
        }

        if (props) {
            return <ComponentToRender {...props}/>;
        }

        return <ComponentToRender/>;
    }
}

function Route<T, K>({
    name: _name,
    condition: _condition,
    defaultView: _defaultView,
    props: _props
}: RouteProps<T, K>) {
    return null;
}

export default LazyRouter;
export { Route };
